import { Mail, Clock, Calendar, Sparkles, MessageCircle, ArrowRight, Languages, Megaphone } from 'lucide-react';
import { useLang } from '../../contexts/LanguageContext';

// Direct-contact section. Phone digits never appear in the DOM text —
// they live only inside the wa.me href. Mailto subject/body adapts to
// the active language so the recipient sees a natural opener.

const RECIPIENT_EMAIL = 'dpetrov@kaizenkanri.nl';
const WHATSAPP_NUMBER = '31613225788'; // intentionally not rendered

const TRUST_ICONS = [Clock, Calendar, Sparkles];

export const ContactSection = () => {
  const { t } = useLang('contact');

  const mailtoHref = `mailto:${RECIPIENT_EMAIL}` +
    `?subject=${encodeURIComponent(t.mailSubject || 'Kaizen Kanri')}` +
    `&body=${encodeURIComponent(t.mailBody || '')}`;
  const whatsappHref = `https://wa.me/${WHATSAPP_NUMBER}` +
    `?text=${encodeURIComponent(t.whatsappGreeting || '')}`;

  return (
    <section id="contact" className="py-28 px-4 sm:px-6 lg:px-8 bg-[#111111] relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-[#00CED1]/5 via-transparent to-transparent pointer-events-none" />
      <div className="absolute top-20 right-10 w-[480px] h-[480px] bg-[#00CED1]/10 rounded-full blur-[160px] pointer-events-none" />

      <div className="max-w-6xl mx-auto relative z-10">
        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-14 lg:gap-20 items-start">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <span className="h-1 w-8 bg-[#00CED1] rounded-full" />
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#00CED1]">{t.eyebrow}</span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-bold tracking-tight text-white leading-[1.1] mb-5">
              {t.titleA}<br />
              <span className="text-[#00CED1]">{t.titleB}</span>
            </h2>
            <p className="text-lg text-white/55 leading-relaxed mb-8 max-w-lg">
              {t.lead}
            </p>

            <div className="space-y-4 mb-8">
              {(t.trust || []).map((item, i) => {
                const Icon = TRUST_ICONS[i] || Clock;
                return (
                  <div key={item.title} className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#00CED1]/10 border border-[#00CED1]/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="h-5 w-5 text-[#00CED1]" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-white">{item.title}</p>
                      <p className="text-xs text-white/40 mt-0.5">{item.sub}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="relative">
            <div className="absolute -inset-2 bg-gradient-to-br from-[#00CED1]/20 to-transparent rounded-3xl blur-xl pointer-events-none" />
            <div className="relative rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.04] to-white/[0.01] backdrop-blur-sm p-7 sm:p-9">
              <div className="flex items-center gap-2 mb-5">
                <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
                <span className="text-xs font-medium text-white/50">{t.cardStatus}</span>
              </div>

              <h3 className="text-2xl font-semibold text-white mb-2">{t.cardTitle}</h3>
              <p className="text-sm text-white/55 mb-7">
                {t.cardSub}
              </p>

              <a
                href={mailtoHref}
                className="group block w-full mb-3 rounded-xl bg-[#00CED1] hover:bg-[#00b4b7] text-[#0a0a0a] font-semibold transition-all shadow-lg shadow-[#00CED1]/20 hover:-translate-y-px active:translate-y-0"
                data-testid="contact-email-cta"
              >
                <span className="flex items-center justify-between gap-3 px-5 py-4">
                  <span className="flex items-center gap-3">
                    <span className="w-10 h-10 rounded-lg bg-[#0a0a0a]/10 flex items-center justify-center">
                      <Mail className="h-5 w-5" />
                    </span>
                    <span className="flex flex-col items-start leading-tight">
                      <span className="text-base font-bold">{t.emailTitle}</span>
                      <span className="text-[11px] font-medium text-[#0a0a0a]/70">{t.emailSub}</span>
                    </span>
                  </span>
                  <ArrowRight className="h-5 w-5 group-hover:translate-x-0.5 transition-transform" />
                </span>
              </a>

              <a
                href={whatsappHref}
                target="_blank"
                rel="noreferrer"
                className="group block w-full rounded-xl bg-white/[0.04] hover:bg-[#25D366]/10 border border-white/10 hover:border-[#25D366]/40 text-white font-semibold transition-all hover:-translate-y-px active:translate-y-0"
                data-testid="contact-whatsapp-cta"
                aria-label="Chat on WhatsApp"
              >
                <span className="flex items-center justify-between gap-3 px-5 py-4">
                  <span className="flex items-center gap-3">
                    <span className="w-10 h-10 rounded-lg bg-[#25D366]/15 border border-[#25D366]/25 flex items-center justify-center">
                      <MessageCircle className="h-5 w-5 text-[#25D366]" />
                    </span>
                    <span className="flex flex-col items-start leading-tight">
                      <span className="text-base font-bold">{t.whatsappTitle}</span>
                      <span className="text-[11px] font-medium text-white/45">{t.whatsappSub}</span>
                    </span>
                  </span>
                  <ArrowRight className="h-5 w-5 text-white/40 group-hover:text-[#25D366] group-hover:translate-x-0.5 transition-all" />
                </span>
              </a>

              <p className="text-[11px] text-center text-white/30 mt-5">
                {t.footnote}
              </p>
            </div>
          </div>
        </div>

        {/* Language & communication — stated plainly, before anyone invests time */}
        {t.language && (
          <div id="language" className="mt-20 scroll-mt-24" data-testid="contact-language-section">
            <div className="rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.04] to-white/[0.01] p-7 sm:p-10">
              <div className="flex items-center gap-2 mb-4">
                <Languages className="h-4 w-4 text-[#00CED1]" />
                <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#00CED1]">{t.language.eyebrow}</span>
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold tracking-tight text-white leading-tight mb-4 max-w-2xl">
                {t.language.title}
              </h3>
              <p className="text-base text-white/60 leading-relaxed max-w-3xl mb-8">
                {t.language.body}
              </p>
              <div className="grid sm:grid-cols-3 gap-5 mb-8">
                {(t.language.bullets || []).map((b) => (
                  <div key={b.title} className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                    <p className="text-sm font-semibold text-white leading-snug">{b.title}</p>
                    <p className="text-xs text-white/45 mt-1.5 leading-relaxed">{b.sub}</p>
                  </div>
                ))}
              </div>
              <p className="text-sm text-white/55 leading-relaxed max-w-3xl">
                {t.language.closing}
              </p>
              <p className="mt-5 inline-flex items-start gap-2 rounded-lg border border-[#00CED1]/25 bg-[#00CED1]/[0.07] px-4 py-2.5 text-xs text-white/70 leading-relaxed">
                <Megaphone className="h-4 w-4 text-[#00CED1] mt-0.5 shrink-0" />
                {t.language.announce}
              </p>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
